import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { LoadingState } from '../../components/common/LoadingState';
import { ApiErrorFallback } from '../../components/common/ApiErrorFallback';
import { EmptyState } from '../../components/common/EmptyState';
import { useUIStore } from '../../stores/ui-store';
import { EquipmentForm } from './EquipmentForm';
import {
  usePlatformEquipmentList,
  useDeletePlatformEquipment,
  useReactivatePlatformEquipment,
} from './api';
import {
  PlatformEquipmentSummaryResponse,
  getEquipmentCategoryName,
  EquipmentCategory
} from './types';

export function PlatformEquipmentPageFixed() {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<EquipmentCategory | undefined>();
  const [includeInactive, setIncludeInactive] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState<PlatformEquipmentSummaryResponse | undefined>();
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');

  const [searchParams, setSearchParams] = useSearchParams();
  const { addNotification } = useUIStore();
  const deleteMutation = useDeletePlatformEquipment();
  const reactivateMutation = useReactivatePlatformEquipment();

  // Fetch equipment list with current filters
  const { data, isLoading, error, refetch } = usePlatformEquipmentList({
    page: currentPage,
    pageSize: pageSize,
    search: searchTerm || undefined,
    category: selectedCategory,
    includeInactive,
  });

  const handleCreateNew = () => {
    setEditingEquipment(undefined);
    setFormMode('create');
    setIsFormOpen(true);
  };

  // Handle URL parameters for quick actions
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'new') {
      handleCreateNew();
      // Clean up the URL parameter
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const handleEdit = (equipment: PlatformEquipmentSummaryResponse) => {
    setEditingEquipment(equipment);
    setFormMode('edit');
    setIsFormOpen(true);
  };

  const handleDelete = async (equipment: PlatformEquipmentSummaryResponse) => {
    if (!window.confirm(`Are you sure you want to deactivate "${equipment.name}"?`)) {
      return;
    }

    try {
      await deleteMutation.mutateAsync({ id: equipment.id });
      addNotification({
        type: 'success',
        title: 'Equipment Deactivated',
        message: `${equipment.name} has been deactivated successfully.`,
      });
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Failed to Deactivate Equipment',
        message: error.message || 'An unexpected error occurred.',
      });
    }
  };

  const handleReactivate = async (equipment: PlatformEquipmentSummaryResponse) => {
    try {
      await reactivateMutation.mutateAsync({ id: equipment.id });
      addNotification({
        type: 'success',
        title: 'Equipment Reactivated',
        message: `${equipment.name} has been reactivated successfully.`,
      });
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Failed to Reactivate Equipment',
        message: error.message || 'An unexpected error occurred.',
      });
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page when searching
    refetch();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory(undefined);
    setIncludeInactive(false);
    setCurrentPage(1);
  };

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">Platform Equipment</h1>
        </div>
        <ApiErrorFallback 
          error={error}
          onRetry={() => refetch()}
          title="Unable to load equipment"
          description="There was a problem loading the equipment list."
          showDetails={true}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Platform Equipment</h1>
          <p className="text-gray-600 text-sm">
            Manage global equipment library available to all studios
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button onClick={handleCreateNew}>
            Create New Equipment
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-3">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-2 items-center">
            <div className="flex-1 min-w-[200px]">
              <Input
                id="search"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search equipment..."
                className="h-8 text-sm"
              />
            </div>
            
            <select
              id="category"
              value={selectedCategory || ''}
              onChange={(e) => setSelectedCategory(e.target.value ? parseInt(e.target.value) as EquipmentCategory : undefined)}
              className="h-8 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[120px]"
            >
              <option value="">All Categories</option>
              {Object.values(EquipmentCategory)
                .filter(value => typeof value === 'number')
                .map((category) => (
                  <option key={category} value={category}>
                    {getEquipmentCategoryName(category as EquipmentCategory)}
                  </option>
                ))}
            </select>

            <div className="flex items-center space-x-1">
              <input
                id="includeInactive"
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="includeInactive" className="text-xs text-gray-700 whitespace-nowrap">
                Inactive
              </label>
            </div>

            <div className="flex space-x-1">
              <Button type="submit" variant="outline" size="sm" className="h-8 px-3">
                Search
              </Button>
              <Button type="button" variant="outline" size="sm" className="h-8 px-3" onClick={clearFilters}>
                Clear
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Search Results Summary */}
      {searchTerm && data?.equipment && (
        <div className="text-sm text-gray-600 mb-4">
          Found {data.equipment.length} result{data.equipment.length !== 1 ? 's' : ''} for "{searchTerm}"
          {data.pagination && data.pagination.totalCount > data.equipment.length && (
            <span> (showing {data.equipment.length} of {data.pagination.totalCount} total)</span>
          )}
        </div>
      )}

      {/* Equipment Table */}
      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <LoadingState 
              message="Loading equipment..."
              subMessage="Please wait while we fetch your data."
              variant="spinner"
            />
          ) : !data?.equipment.length ? (
            <EmptyState
              title="No equipment found"
              description={
                searchTerm || selectedCategory || includeInactive
                  ? 'Try adjusting your filters or create new equipment.'
                  : 'Get started by creating your first piece of equipment.'
              }
              actionLabel="Create New Equipment"
              onAction={handleCreateNew}
              showCard={false}
            />
          ) : (
            <>
              {/* Top Pagination Info */}
              {data.pagination && (
                <div className="flex justify-between items-center mb-4 py-2 border-b border-gray-100">
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <span>
                      Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, data.pagination.totalCount)} of {data.pagination.totalCount}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(parseInt(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value={10}>10 per page</option>
                      <option value={15}>15 per page</option>
                      <option value={25}>25 per page</option>
                      <option value={50}>50 per page</option>
                    </select>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage <= 1 || isLoading}
                        className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <span className="px-2 py-1 text-sm">
                        {currentPage} of {data.pagination.totalPages}
                      </span>
                      <button
                        onClick={() => setCurrentPage(Math.min(data.pagination.totalPages, currentPage + 1))}
                        disabled={currentPage >= data.pagination.totalPages || isLoading}
                        className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Simple Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-3 font-medium text-gray-700">Name</th>
                      <th className="text-left py-3 px-3 font-medium text-gray-700">Category</th>
                      <th className="text-left py-3 px-3 font-medium text-gray-700">Setup Required</th>
                      <th className="text-left py-3 px-3 font-medium text-gray-700">Status</th>
                      <th className="text-right py-3 px-3 font-medium text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.equipment.map((equipment) => (
                      <tr key={equipment.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-3">
                          <div>
                            <div className="font-medium text-gray-900">{equipment.name}</div>
                            {equipment.description && (
                              <div className="text-sm text-gray-500 mt-1">
                                {equipment.description.length > 60 ? (
                                  `${equipment.description.substring(0, 60)}...`
                                ) : (
                                  equipment.description
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-3">
                          <span className="text-gray-700">
                            {getEquipmentCategoryName(equipment.category)}
                          </span>
                        </td>
                        <td className="py-3 px-3">
                          {equipment.requiresSetup ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              Yes
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              No
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-3">
                          {equipment.isActive ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              Inactive
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-3">
                          <div className="flex justify-end space-x-1">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEdit(equipment)}
                            >
                              Edit
                            </Button>
                            {equipment.isActive ? (
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDelete(equipment)}
                                disabled={deleteMutation.isPending}
                              >
                                {deleteMutation.isPending ? 'Deactivating...' : 'Deactivate'}
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                onClick={() => handleReactivate(equipment)}
                                disabled={reactivateMutation.isPending}
                              >
                                {reactivateMutation.isPending ? 'Reactivating...' : 'Reactivate'}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Equipment Form Modal */}
      <EquipmentForm
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        equipment={editingEquipment}
        mode={formMode}
      />
    </div>
  );
}